// Profile settings the user edits in the apps, beyond the GTD contexts (whose
// rules live with the rest of GTD in para.ts): the auto-filing threshold,
// renamed contexts, and searching the database's list of time zones.

/** The thresholds offered, 50% to 95% in steps of 5%. */
export const CONFIDENCE_THRESHOLDS: readonly number[] = Array.from({ length: 10 }, (_, i) => (50 + 5 * i) / 100);

/** A threshold the app saves: 0.50 to 0.95 with at most two decimals (the column is DECIMAL(3,2)). */
export function isConfidenceThreshold(value: unknown): value is number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return false;
  const hundredths = Math.round(value * 100);
  return Math.abs(value * 100 - hundredths) < 1e-6 && hundredths >= 50 && hundredths <= 95;
}

/**
 * Old name to new name for each context renamed in an edited list. Each item
 * carries the name it started with; an added one has none. A blank or
 * unchanged name is not a rename.
 */
export function contextRenames(items: readonly { original: string | null; value: string }[]): Map<string, string> {
  const renames = new Map<string, string>();
  for (const item of items) {
    const value = item.value.trim();
    if (item.original !== null && value !== '' && value !== item.original) renames.set(item.original, value);
  }
  return renames;
}

const words = (text: string) => text.toLowerCase().replace(/[_/]+/g, ' ').split(/\s+/).filter(Boolean);

/**
 * The time zones whose name holds every word of the query, in any order,
 * ignoring case and underscores. `zones` is the database's own list
 * (time_zone_names()).
 */
export function matchTimeZones(query: string, zones: readonly string[]): string[] {
  const wanted = words(query);
  if (wanted.length === 0) return [...zones];
  return zones.filter(zone => {
    const name = zone.toLowerCase().replace(/[_/]+/g, ' ');
    return wanted.every(word => name.includes(word));
  });
}
